"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { useIsApproved } from "@/components/auth/useIsApproved";
import { useIsSignedIn } from "@/components/auth/useIsSignedIn";
import type { MarketingStrings } from "@/lib/i18n/marketing-strings";
import { localeHref, languageToggleTarget } from "@/lib/i18n/es-path";

type Lang = "EN" | "ES";

// Step 10.5 commit 1: Tesla-style fixed nav. Transparent over the hero,
// switches to a white/blurred background after the user scrolls past
// 100px. The right-side pill button is locale-aware AND auth-aware:
// signed-out -> "Apply" -> /sign-up; signed-in -> "Dashboard" ->
// /dashboard. Per Alexis Jun 12 ruling we do NOT auto-redirect signed-
// in visitors away from /. Tesla/Apple/Patagonia pattern.

export function MarketingNav({
  initialLocale,
  strings,
}: {
  initialLocale: "en" | "es";
  strings: MarketingStrings;
}) {
  // PASTE #233/#234: public locale is path-based. Links carry the /es
  // prefix when the page is rendered in Spanish; the toggle changes the
  // URL so the address bar reflects the language.
  const lh = (href: string) => localeHref(href, initialLocale);
  // Step 10.5 commit 11 v2 / BUG C v2: sentinel-based adaptive nav
  // (Tesla pattern). At top of a hero, nav is transparent with white
  // text; past the hero, nav goes solid white with dark text.
  //
  // The page provides the trigger: every page with a dark hero
  // renders a <div data-marketing-hero-sentinel> at the bottom of
  // its hero. IntersectionObserver watches this sentinel; when it's
  // visible below the fixed nav, we're "on the hero" (transparent).
  // When it leaves the viewport upward, we're past the hero (solid).
  //
  // Pages with no hero (variety detail, signed-in /catalog grid)
  // omit the sentinel; the observer never finds one and the nav
  // stays solid - readable on any white background.
  // Initial state matches Tesla spec: at top of page, transparent
  // (white text). The observer flips to solid the moment the
  // sentinel scrolls out of view, OR immediately on mount if no
  // sentinel is found (no-hero page). The flash from transparent
  // -> solid on no-hero pages is sub-frame because useEffect runs
  // synchronously after the first hydration commit.
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [lang, setLang] = useState<Lang>(initialLocale === "es" ? "ES" : "EN");
  // #84: THREE states, from the two existing shared hooks - no new source of
  // truth. approved -> Dashboard. Signed out -> Apply + Sign in (a returning
  // client landing on magnaflor.com had no way in). Signed in but NOT approved
  // -> Apply only, unchanged (they are already authenticated; a Sign in link
  // would be a dead end, and their application is still pending).
  const approval = useIsApproved();
  const session = useIsSignedIn();
  const showDashboard = approval === "approved";
  const showSignIn = session === "unauthed";

  useEffect(() => {
    const sentinel = document.querySelector(
      "[data-marketing-hero-sentinel]",
    );
    if (!sentinel) {
      // No hero on this page - stay solid for legibility.
      // eslint-disable-next-line react-hooks/set-state-in-effect -- MKT-#82(e): depends on a client-only DOM query (querySelector); the mount-effect setState is intentional + shipped. Refactor tracked separately.
      setScrolled(true);
      return;
    }
    // rootMargin -72px accounts for the fixed nav height so the
    // intersection toggle fires exactly when the sentinel reaches
    // the bottom of the nav (i.e. the hero is leaving the visible
    // viewport top-down).
    const observer = new IntersectionObserver(
      ([entry]) => {
        // entry.isIntersecting === true -> sentinel is below the
        // nav, hero still in view -> transparent.
        // false -> we've scrolled past the hero -> solid.
        setScrolled(!entry.isIntersecting);
      },
      { rootMargin: "-72px 0px 0px 0px", threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  function handleLangChange(next: Lang) {
    setLang(next);
    const isES = next === "ES";
    // Persist the choice (so any un-prefixed link still renders the
    // chosen language) AND move the URL so it reflects the language.
    document.cookie = `mag-locale=${isES ? "es" : "en"}; path=/; max-age=31536000; samesite=lax`;
    // #234: read the REAL address-bar path (usePathname reports the
    // rewritten bare path on /es pages, which made ES->EN a no-op) and
    // hard-navigate so the URL always changes, preserving query + hash.
    const targetPath = languageToggleTarget(window.location.pathname, isES);
    window.location.assign(
      targetPath + window.location.search + window.location.hash,
    );
  }

  const navClass = scrolled
    ? "bg-white/95 shadow-[0_1px_0_rgba(0,0,0,0.05)] backdrop-blur-xl"
    : "bg-transparent";
  const linkColor = scrolled ? "text-brand-text" : "text-white";
  const logoColor = scrolled ? "text-brand-text" : "text-white";
  const pillClass = scrolled
    ? "bg-brand-teal text-white hover:bg-brand-teal-dark"
    : "bg-white/15 text-white backdrop-blur hover:bg-white/25";

  // SSR default = "Apply" (the marketing CTA visitors expect). Once the
  // client auth check resolves, swap to "Dashboard" only if a session
  // is found. Server-rendered HTML always contains the Apply pill so
  // search engines + JS-disabled visitors see the primary conversion.
  // /dashboard is portal (no /es variant); /sign-up localizes.
  const ctaHref = showDashboard ? "/dashboard" : lh("/sign-up");
  const ctaLabel = showDashboard ? strings.nav.dashboard : strings.nav.apply;

  return (
    <nav
      aria-label={strings.nav.mainNavAria}
      className={`fixed left-0 right-0 top-0 z-50 flex items-center justify-between px-5 py-4 transition-all duration-200 md:px-10 ${navClass}`}
    >
      <Link
        href={lh("/")}
        aria-label={strings.nav.homeAria}
        className={`text-xl font-bold tracking-tight transition-colors ${logoColor}`}
      >
        MAGNAFLOR
      </Link>

      <div className="hidden items-center gap-8 md:flex">
        {/* Step 10.5 commit 10 / BUG A fix: every nav link points at
            a real route (not a hash anchor). "How it works" jumps to
            the homepage anchor when on /, navigates to homepage
            first when on any other route. */}
        <Link
          href={lh("/how-it-works")}
          className={`text-sm font-medium transition-opacity hover:opacity-70 ${linkColor}`}
        >
          {strings.nav.howItWorks}
        </Link>
        <Link
          href={lh("/catalog")}
          className={`text-sm font-medium transition-opacity hover:opacity-70 ${linkColor}`}
        >
          {strings.nav.catalog}
        </Link>
        <Link
          href={lh("/about")}
          className={`text-sm font-medium transition-opacity hover:opacity-70 ${linkColor}`}
        >
          {strings.nav.about}
        </Link>
        <Link
          href={lh("/contact")}
          className={`text-sm font-medium transition-opacity hover:opacity-70 ${linkColor}`}
        >
          {strings.nav.contact}
        </Link>
        <LangToggle
          lang={lang}
          onChange={handleLangChange}
          variant={scrolled ? "light" : "dark"}
          label={strings.nav.languageAria}
        />
        {showSignIn && (
          <Link
            href={lh("/sign-in")}
            data-testid="marketing-nav-signin"
            className={`text-sm font-medium transition-opacity hover:opacity-70 ${linkColor}`}
          >
            {strings.nav.signIn}
          </Link>
        )}
        <Link
          href={ctaHref}
          data-testid="marketing-nav-cta"
          className={`rounded-full px-5 py-2.5 text-xs font-semibold tracking-wide transition-colors ${pillClass}`}
        >
          {ctaLabel}
        </Link>
      </div>

      <button
        type="button"
        aria-label={mobileOpen ? strings.nav.closeMenuAria : strings.nav.openMenuAria}
        aria-expanded={mobileOpen}
        aria-controls="marketing-mobile-nav"
        onClick={() => setMobileOpen((open) => !open)}
        className={`-mr-1 inline-flex h-11 w-11 items-center justify-center rounded-md md:hidden ${linkColor}`}
      >
        <HamburgerIcon open={mobileOpen} />
      </button>

      {mobileOpen && (
        <div
          id="marketing-mobile-nav"
          className="absolute left-0 right-0 top-full mt-0 border-t border-brand-border bg-white shadow-lg md:hidden"
        >
          <ul className="flex flex-col px-5 py-2 text-brand-text">
            <li>
              <Link
                href={lh("/how-it-works")}
                onClick={() => setMobileOpen(false)}
                className="block py-3.5 text-base font-medium"
              >
                {strings.nav.howItWorks}
              </Link>
            </li>
            <li>
              <Link
                href={lh("/catalog")}
                onClick={() => setMobileOpen(false)}
                className="block py-3.5 text-base font-medium"
              >
                {strings.nav.catalog}
              </Link>
            </li>
            <li>
              <Link
                href={lh("/about")}
                onClick={() => setMobileOpen(false)}
                className="block py-3.5 text-base font-medium"
              >
                {strings.nav.about}
              </Link>
            </li>
            <li>
              <Link
                href={lh("/contact")}
                onClick={() => setMobileOpen(false)}
                className="block py-3.5 text-base font-medium"
              >
                {strings.nav.contact}
              </Link>
            </li>
            {showSignIn && (
              <li>
                <Link
                  href={lh("/sign-in")}
                  data-testid="marketing-nav-signin-mobile"
                  onClick={() => setMobileOpen(false)}
                  className="block py-3.5 text-base font-medium"
                >
                  {strings.nav.signIn}
                </Link>
              </li>
            )}
            <li>
              <Link
                href={ctaHref}
                onClick={() => setMobileOpen(false)}
                className="my-2 inline-flex rounded-full bg-brand-teal px-5 py-2.5 text-xs font-semibold text-white"
              >
                {ctaLabel}
              </Link>
            </li>
            <li className="py-3">
              <LangToggle
                lang={lang}
                onChange={(next) => {
                  handleLangChange(next);
                  setMobileOpen(false);
                }}
                variant="light"
                label={strings.nav.languageAria}
              />
            </li>
          </ul>
        </div>
      )}
    </nav>
  );
}

function LangToggle({
  lang,
  onChange,
  variant,
  label,
}: {
  lang: Lang;
  onChange: (next: Lang) => void;
  variant: "light" | "dark";
  label: string;
}) {
  const baseBorder =
    variant === "light"
      ? "border-brand-border bg-white"
      : "border-white/30 bg-white/10";
  const inactiveText =
    variant === "light" ? "text-brand-text-muted" : "text-white/80";
  return (
    <div
      role="group"
      aria-label={label}
      className={`inline-flex items-center rounded-full border p-0.5 text-xs font-semibold ${baseBorder}`}
    >
      {(["EN", "ES"] as const).map((code) => {
        const active = lang === code;
        return (
          <button
            key={code}
            type="button"
            onClick={() => onChange(code)}
            aria-pressed={active}
            className={
              active
                ? "rounded-full bg-brand-teal px-4 py-2 text-white"
                : `rounded-full px-4 py-2 transition-colors ${inactiveText}`
            }
          >
            {code}
          </button>
        );
      })}
    </div>
  );
}

function HamburgerIcon({ open }: { open: boolean }) {
  return (
    <svg
      aria-hidden="true"
      width="22"
      height="22"
      viewBox="0 0 22 22"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      {open ? (
        <>
          <line x1="5" y1="5" x2="17" y2="17" />
          <line x1="17" y1="5" x2="5" y2="17" />
        </>
      ) : (
        <>
          <line x1="3" y1="7" x2="19" y2="7" />
          <line x1="3" y1="11" x2="19" y2="11" />
          <line x1="3" y1="15" x2="19" y2="15" />
        </>
      )}
    </svg>
  );
}
